import { useState } from 'react';

interface ModuleCardProps {
  id: string;
  icon: string;
  title: string;
  description: string;
  color: string;
  features: string[];
  grade: string;
}

const colorMap: Record<string, { bg: string; text: string; hover: string; badge: string; gradient: string }> = {
  pink: { 
    bg: 'bg-pink-50', 
    text: 'text-pink-600', 
    hover: 'hover:border-pink-300', 
    badge: 'bg-pink-100 text-pink-700',
    gradient: 'bg-gradient-to-r from-pink-500 to-pink-600'
  },
  blue: { 
    bg: 'bg-blue-50', 
    text: 'text-blue-600', 
    hover: 'hover:border-blue-300', 
    badge: 'bg-blue-100 text-blue-700',
    gradient: 'bg-gradient-to-r from-blue-500 to-blue-600'
  },
  green: { 
    bg: 'bg-green-50', 
    text: 'text-green-600', 
    hover: 'hover:border-green-300', 
    badge: 'bg-green-100 text-green-700',
    gradient: 'bg-gradient-to-r from-green-500 to-green-600'
  },
  purple: { 
    bg: 'bg-purple-50', 
    text: 'text-purple-600', 
    hover: 'hover:border-purple-300', 
    badge: 'bg-purple-100 text-purple-700',
    gradient: 'bg-gradient-to-r from-purple-500 to-purple-600'
  },
  orange: { 
    bg: 'bg-orange-50', 
    text: 'text-orange-600', 
    hover: 'hover:border-orange-300', 
    badge: 'bg-orange-100 text-orange-700',
    gradient: 'bg-gradient-to-r from-orange-500 to-orange-600'
  },
  indigo: { 
    bg: 'bg-indigo-50', 
    text: 'text-indigo-600', 
    hover: 'hover:border-indigo-300', 
    badge: 'bg-indigo-100 text-indigo-700',
    gradient: 'bg-gradient-to-r from-indigo-500 to-indigo-600'
  },
  teal: { 
    bg: 'bg-teal-50', 
    text: 'text-teal-600', 
    hover: 'hover:border-teal-300', 
    badge: 'bg-teal-100 text-teal-700',
    gradient: 'bg-gradient-to-r from-teal-500 to-teal-600'
  },
  cyan: { 
    bg: 'bg-cyan-50', 
    text: 'text-cyan-600', 
    hover: 'hover:border-cyan-300', 
    badge: 'bg-cyan-100 text-cyan-700',
    gradient: 'bg-gradient-to-r from-cyan-500 to-cyan-600'
  }
};

export default function ModuleCard({ icon, title, description, color, features, grade, id }: ModuleCardProps) {
  const colors = colorMap[color] || colorMap.pink;
  const [showModal, setShowModal] = useState(false);

  const handleStartExperience = () => {
    // 已开发的模块直接跳转
    const developedModules: Record<string, string> = {
      'magic-drawing': '/primary/magic-drawing',
      'smart-companion': '/primary/smart-companion',
      'image-recognition': '/primary/image-recognition',
      'voice-assistant': '/primary/voice-assistant',
      'algorithm-games': '/primary/algorithm-games',
      'creative-workshop': '/primary/creative-workshop',
      'coding-game': '/primary/coding-game',
      'computational-thinking': '/primary/computational-thinking',
      'ai-recognition': '/primary/ai-recognition',
      'story-creator': '/primary/story-creator',
      'visual-programming': '/primary/visual-programming'
    };

    if (developedModules[id]) {
      window.REACT_APP_NAVIGATE(developedModules[id]);
    } else {
      // 未开发的模块显示提示
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <div className={`bg-white rounded-2xl shadow-lg border-2 border-gray-100 ${colors.hover} transition-all hover:shadow-2xl cursor-pointer group`}>
        <div className={`${colors.bg} p-8 rounded-t-2xl`}>
          <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
            <i className={`${icon} text-4xl ${colors.text}`}></i>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600">{description}</p>
        </div>

        <div className="p-6">
          <div className={`inline-block ${colors.badge} px-3 py-1 rounded-full text-xs font-semibold mb-4`}>
            {grade}
          </div>

          <div className="space-y-2 mb-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 text-gray-700">
                <i className={`ri-checkbox-circle-fill ${colors.text}`}></i>
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>

          <button 
            onClick={handleStartExperience}
            className={`w-full px-6 py-3 ${colors.gradient} text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer`}
          >
            开始体验
          </button>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4" onClick={handleCloseModal}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button 
              onClick={handleCloseModal}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <div className={`w-20 h-20 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-6`}>
              <i className={`${icon} text-5xl ${colors.text}`}></i>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">{title}</h3>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
                <div>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    该模块正在开发中，即将上线！如需体验完整功能，请联系学校管理员开通账号。
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3 text-gray-600">
                <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <i className={`ri-user-line ${colors.text}`}></i>
                </div>
                <span className="text-sm">需要学生账号登录</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-600">
                <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <i className={`ri-shield-check-line ${colors.text}`}></i>
                </div>
                <span className="text-sm">安全的学习环境</span>
              </div>
              <div className="flex items-center space-x-3 text-gray-600">
                <div className={`w-8 h-8 ${colors.bg} rounded-lg flex items-center justify-center`}>
                  <i className={`ri-trophy-line ${colors.text}`}></i>
                </div>
                <span className="text-sm">记录学习成长轨迹</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button 
                onClick={handleCloseModal}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
              >
                我知道了
              </button>
              <button 
                onClick={() => {
                  handleCloseModal();
                  document.querySelector('#vapi-widget-floating-button')?.dispatchEvent(new MouseEvent('click', { bubbles: true }));
                }}
                className={`flex-1 px-6 py-3 ${colors.gradient} text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer`}
              >
                联系咨询
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
